import { useLayoutEffect, useState } from 'react';
import {
  clearWizardState,
  restoreWizardState,
  useWizardPersistence,
} from './use-wizard-persistence';

// The capture fields the backend's src/capture/ pipeline reads. Shared by the
// daily log, the visitor log and the parcel desk so all three speak one shape.
export interface CaptureFields {
  // Which real unit the entry is about. Nullable: the desk logs "loading bay"
  // and units that are not in the inventory yet, and that has to keep working.
  unit_id: number | null;
  // Keyed by field ('name', 'lastName'), or indexed for arrays ('names.0').
  // `raw` is sent as PLAINTEXT — the server encrypts it and refuses to trust a
  // client-supplied ciphertext.
  field_provenance: Record<
    string,
    { source: 'manual' | 'scan' | 'scan_edited'; raw?: string; confidence?: number }
  >;
}

export interface DailyLogState extends CaptureFields {
  buildingActivity: string;
  name: string;
  // Migration 044: documents routinely print three name parts.
  middleName: string;
  lastName: string;
  // daily_log.name IS a resident — the form label says so verbatim — so it
  // resolves to a person. unset | single_match | picked | no_match; there is
  // deliberately no value meaning "ambiguous and nobody chose".
  subject_occupancy_id: string | null;
  subject_resolution: 'unset' | 'single_match' | 'picked' | 'no_match';
  apartmentLocation: string;
  priority: string;
  description: string;
  additionalNotes: string;
  userId: string | undefined;
  notes: string;
  details: string;
  property_ids: number[] | [] | undefined;
  createdAt?: string;
}

/** The sessionStorage bucket this wizard owns. */
const WIZARD_FLOW = 'activity';

const useDailyLogHandler = (
  userId: string | undefined,
  property_ids: number[] | [] | undefined
) => {
  const initialState: DailyLogState = {
    buildingActivity: '',
    name: '',
    middleName: '',
    lastName: '',
    subject_occupancy_id: null,
    subject_resolution: 'unset',
    unit_id: null,
    field_provenance: {},
    apartmentLocation: '',
    priority: '',
    notes: '',
    details: '',
    description: '',
    additionalNotes: '',
    userId: userId || '',
    property_ids: property_ids || [],
  };

  /* Same contract as the visitor wizard — see use-wizard-persistence.ts. */
  const [dailyLogState, setDailyLogState] = useState<DailyLogState>(() => {
    const restored = restoreWizardState<DailyLogState>(WIZARD_FLOW, userId);

    return restored
      ? { ...initialState, ...restored, userId, property_ids: property_ids || [] }
      : initialState;
  });
  const activePropertyId = property_ids?.[0] ?? null;

  useLayoutEffect(() => {
    const nextPropertyIds = activePropertyId === null ? [] : [activePropertyId];

    setDailyLogState(previous => {
      const propertyIdUnchanged =
        (previous.property_ids?.[0] ?? null) === activePropertyId;

      if (propertyIdUnchanged && previous.userId === userId) {
        return previous;
      }

      return {
        ...previous,
        userId,
        property_ids: nextPropertyIds,
      };
    });
  }, [activePropertyId, userId]);

  const dailyLogChangeHandler = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
      | string,
    fieldName?: string,
    explicitValue?: string
  ) => {
    // eslint-disable-next-line
    let value: string | undefined;

    /* A STRING ARGUMENT IS THE VALUE — it is not a marker that the value is
       somewhere else.
       This branch used to read `explicitValue`, which is undefined for the many
       callers that pass two arguments (`changeHandler(label,
       'apartmentLocation')` in location.tsx:97, `changeHandler(next, 'name')`
       in name-and-last-name.tsx:94/119/129 and the roster auto-fill at 148-150).
       Those calls wrote `undefined` into state, and the next render read
       `.trim()` off it — a TypeError inside UnitSelect and PersonField that took
       the Maintenance, Miscellaneous, Shift Change and Resident flows down
       entirely. The visitor wizard has always resolved it this way
       (use-visitor-log-handler.tsx:167-172); this is the same rule, so the two
       cannot disagree again. */
    if (explicitValue !== undefined) {
      value = explicitValue;
    } else if (typeof e === 'string') {
      value = e;
    } else if ('value' in e.target) {
      value = e.target.value;
    }

    if (fieldName) {
      setDailyLogState(prevState => ({
        ...prevState,
        [fieldName]: value,
      }));
    }
  };

  // Patch setter for the capture fields.
  //
  // changeHandler is string-only by construction — it reads e.target.value —
  // and the capture fields are a number (unit_id), a nullable uuid
  // (subject_occupancy_id) and an object (field_provenance). Widening
  // changeHandler to carry those would loosen the type on every existing call
  // site in the wizard to buy nothing, so they get their own door.
  const setCapture = (patch: Partial<DailyLogState>) => {
    setDailyLogState(prev => ({ ...prev, ...patch }));
  };

  const dailyLogFinish = () => {
    setDailyLogState(initialState);
    clearWizardState(WIZARD_FLOW, userId);
  };

  /* "Nothing typed yet" for the activities flow. Note the SINGULAR name fields:
     unlike the visitor wizard, this flow records one person, not a party. */
  const isEmptyEntry =
    !dailyLogState.buildingActivity &&
    !(dailyLogState.name || '').trim() &&
    !(dailyLogState.lastName || '').trim() &&
    !dailyLogState.apartmentLocation &&
    !dailyLogState.notes &&
    !dailyLogState.details;

  useWizardPersistence(WIZARD_FLOW, dailyLogState, isEmptyEntry, userId);

  return { dailyLogState, dailyLogChangeHandler, dailyLogFinish, setCapture };
};

export default useDailyLogHandler;
